import { ArrowLeft, ArrowRight, Loader2, Save, Building2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import AuthenticatedLayout from '../components/AuthenticatedLayout'
import CT600PlanSelectionModal from '../components/CT600PlanSelectionModal'
import CT600WizardSidebar from '../components/ct600/CT600WizardSidebar'
import CT600StepNav from '../components/ct600/CT600StepNav'
import WizardDetailsModal from '../components/wizard/WizardDetailsModal'
import CT600StepSetup from '../components/ct600/CT600StepSetup'
import CT600StepCompanyInfo from '../components/ct600/CT600StepCompanyInfo'
import CT600StepFinancials from '../components/ct600/CT600StepFinancials'
import CT600StepTaxComp from '../components/ct600/CT600StepTaxComp'
import CT600StepSupplementary from '../components/ct600/CT600StepSupplementary'
import CT600StepReview from '../components/ct600/CT600StepReview'
import CT600StepSubmit from '../components/ct600/CT600StepSubmit'
import TrustpilotReviewModal from '../components/TrustpilotReviewModal'
import { useFormPayment } from '../hooks/useFormPayment'
import { useUserProfile } from '../context/UserProfileContext'
import type {
  Ct600CalculationBreakdown,
  Ct600Form,
  Ct600PeriodSplitData,
  Ct600PeriodSubmission,
  Ct600Status,
  Ct600SubmitPeriodResult,
  StoredCompany,
  GenericRecord,
} from '../types'
import { ApiError } from '../utils/api'
import paymentService from '../utils/paymentService'
import type { PaymentCoverage } from '../utils/paymentService'
import ct600Service from '../utils/ct600Service'
import { companyService } from '../utils/companyService'
import { ct600DraftService } from '../utils/ct600DraftService'
import {
  ct600Steps,
  defaultSetup,
  defaultCompanyInfo,
  defaultFinancialData,
  defaultBalanceSheet,
  defaultPreviousYear,
  defaultTaxComp,
  defaultSupplementary,
  defaultSubmission,
  toFilingScope,
  fromFilingScope,
  toReturnType,
} from '../types/ct600Filing'
import type {
  CT600WizardStepId,
  CT600SetupForm,
  CT600CompanyInfoForm,
  CT600FinancialDataForm,
  CT600BalanceSheetForm,
  CT600PreviousYearForm,
  CT600TaxCompForm,
  CT600SupplementaryState,
  CT600SubmissionForm,
  CT600Draft,
} from '../types/ct600Filing'

// --------------------------------------------------------
// Helpers
// --------------------------------------------------------

const PLAN_STORAGE_KEY = 'ct600PlanSlugs'

function readStoredPlanSlug(formId: string | null): string | null {
  if (!formId) return null
  try {
    const raw = localStorage.getItem(PLAN_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return typeof parsed?.[formId] === 'string' ? parsed[formId] : null
  } catch {
    return null
  }
}

function persistPlanSlug(formId: string | null, planSlug: string | null) {
  if (!formId || !planSlug) return
  try {
    const raw = localStorage.getItem(PLAN_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : {}
    parsed[formId] = planSlug
    localStorage.setItem(PLAN_STORAGE_KEY, JSON.stringify(parsed))
  } catch {
    /* ignore */
  }
}

function extractApiError(
  err: unknown,
  fallback = 'An error occurred. Please try again.'
): string {
  if (err && typeof err === 'object') {
    const e = err as Record<string, any>
    const data = e.response?.data ?? e.response
    if (data) {
      if (data.response?.hmrcResponse?.message)
        return data.response.hmrcResponse.message
      if (data.response?.message) return data.response.message
      if (data.message) return data.message
      if (typeof data.error === 'string') return data.error
    }
    if (e.message) return e.message
  }
  if (typeof err === 'string') return err
  return fallback
}

/**
 * Normalise a date coming back from the API into the `YYYY-MM-DD` form that
 * `<input type="date">` requires. Mongo stores the accounting period as a Date,
 * so it serialises to a full ISO timestamp — feeding that to a date input
 * renders an empty field, which reads as "my data wasn't saved".
 */
function toDateInput(value: unknown): string {
  if (!value) return ''
  const raw = typeof value === 'string' ? value : String(value)
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw
  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) return ''
  // Use the UTC parts: the stored value is midnight UTC, and going through the
  // local timezone would shift it to the previous day west of Greenwich.
  return parsed.toISOString().slice(0, 10)
}

/**
 * True when a section sub-object holds at least one non-zero figure. A CT600 is
 * created with every section already present and zero-filled, so "does the key
 * exist" says nothing about whether the user has filled anything in.
 */
function hasFigures(obj: unknown): boolean {
  if (!obj || typeof obj !== 'object') return false
  return Object.values(obj as Record<string, unknown>).some(
    v => typeof v === 'number' && Number.isFinite(v) && v !== 0
  )
}

function unwrap<T>(payload: unknown): {
  success: boolean
  data: T
  message?: string
} {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    const obj = payload as Record<string, any>
    const msg = typeof obj.message === 'string' ? obj.message : undefined
    const result: { success: boolean; data: T; message?: string } = {
      success: obj.success ?? true,
      data: (obj.data ?? payload) as T,
    }
    if (msg) result.message = msg
    return result
  }
  return { success: true, data: payload as T }
}

function parseSubmissionErrors(payload: unknown): string[] {
  if (!Array.isArray(payload)) return []

  return payload
    .map(item => {
      if (typeof item === 'string') return item
      if (!item || typeof item !== 'object') return null
      const rawMessage =
        typeof (item as Record<string, unknown>).message === 'string'
          ? (item as Record<string, string>).message
          : null
      if (!rawMessage) return null

      try {
        const parsed = JSON.parse(rawMessage) as Record<string, unknown>
        const nestedText =
          parsed?.Error && typeof parsed.Error === 'object'
            ? (parsed.Error as Record<string, unknown>).Text
            : undefined
        if (typeof nestedText === 'string' && nestedText.trim())
          return nestedText
      } catch {
        // message is plain text
      }

      return rawMessage
    })
    .filter((msg): msg is string => Boolean(msg && msg.trim()))
}

function parseSubmitOutcome(payload: unknown): {
  status: Ct600Status
  correlationId?: string
  message?: string
  isLpoa?: boolean
  periods?: Ct600SubmitPeriodResult[]
  submissionReference?: string
  requiresPolling?: boolean
} {
  const root =
    payload && typeof payload === 'object'
      ? (payload as Record<string, unknown>)
      : {}
  const data =
    root.data && typeof root.data === 'object'
      ? (root.data as Record<string, unknown>)
      : root
  const form =
    root.form && typeof root.form === 'object'
      ? (root.form as Record<string, unknown>)
      : data.form && typeof data.form === 'object'
        ? (data.form as Record<string, unknown>)
        : {}

  // New LPOA-aware fields
  const isLpoa = data.isLpoa === true
  const aggregateStatus =
    typeof data.aggregateStatus === 'string'
      ? data.aggregateStatus.toLowerCase()
      : undefined
  const submissionReference =
    typeof data.submissionReference === 'string'
      ? data.submissionReference
      : undefined
  const requiresPolling =
    typeof data.requiresPolling === 'boolean' ? data.requiresPolling : undefined
  const periods = Array.isArray(data.periods)
    ? (data.periods as Ct600SubmitPeriodResult[])
    : Array.isArray((root as Record<string, unknown>).partialPeriods)
      ? ((root as Record<string, unknown>)
          .partialPeriods as Ct600SubmitPeriodResult[])
      : undefined

  const statusCandidates = [
    aggregateStatus,
    data.status,
    data.acknowledgementStatus,
    form.status,
    root.status,
  ]
    .filter((value): value is string => typeof value === 'string')
    .map(value => value.toLowerCase())

  // Aggregate per-period errors
  const perPeriodErrors: string[] = []
  if (periods) {
    for (const p of periods) {
      if (Array.isArray(p?.errors)) {
        for (const e of p.errors) {
          if (e && typeof e.message === 'string')
            perPeriodErrors.push(e.message)
        }
      }
    }
  }

  const errors = parseSubmissionErrors(data.errors ?? root.errors)
  const allErrors = [...errors, ...perPeriodErrors]
  const successFlag = typeof root.success === 'boolean' ? root.success : true

  const isRejected =
    statusCandidates.includes('rejected') ||
    (!successFlag &&
      !statusCandidates.includes('accepted') &&
      !statusCandidates.includes('submitted') &&
      !statusCandidates.includes('processing'))
  const isAccepted = statusCandidates.includes('accepted')
  const isProcessing = statusCandidates.includes('processing')

  const status: Ct600Status = isRejected
    ? 'rejected'
    : isAccepted
      ? 'accepted'
      : isProcessing
        ? 'submitted'
        : 'submitted'

  // Pick a representative correlation ID — first period's, else top-level
  const firstPeriodCorrId = periods?.find(p => p.correlationId)?.correlationId
  const correlationId =
    typeof data.correlationId === 'string'
      ? data.correlationId
      : typeof root.correlationId === 'string'
        ? root.correlationId
        : firstPeriodCorrId

  const message =
    allErrors[0] ||
    (typeof data.message === 'string' ? data.message : undefined) ||
    (typeof root.message === 'string' ? root.message : undefined)

  const result: ReturnType<typeof parseSubmitOutcome> = { status }
  if (correlationId) result.correlationId = correlationId
  if (message) result.message = message
  if (typeof isLpoa === 'boolean') result.isLpoa = isLpoa
  if (periods) result.periods = periods
  if (submissionReference) result.submissionReference = submissionReference
  if (typeof requiresPolling === 'boolean')
    result.requiresPolling = requiresPolling

  return result
}

// --------------------------------------------------------
// Component
// --------------------------------------------------------

export default function CT600Filling() {
  const { id: formIdFromUrl } = useParams<{ id: string }>()
  const { user } = useUserProfile()
  const navigate = useNavigate()
  const draftLoadedRef = useRef(false)
  const justCreatedRef = useRef(false)

  // ---- Core wizard state ----
  const [currentStep, setCurrentStep] = useState<CT600WizardStepId>(1)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [completedSteps, setCompletedSteps] = useState<Set<CT600WizardStepId>>(
    new Set()
  )
  const [formId, setFormId] = useState<string | null>(formIdFromUrl || null)
  const [formStatus, setFormStatus] = useState<Ct600Status | null>(null)

  // ---- Company list ----
  const [companies, setCompanies] = useState<StoredCompany[]>([])
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true)
  const [selectedCompany, setSelectedCompany] = useState<StoredCompany | null>(
    null
  )

  // ---- Step form state ----
  const [setup, setSetup] = useState<CT600SetupForm>(defaultSetup)
  const [companyInfo, setCompanyInfo] =
    useState<CT600CompanyInfoForm>(defaultCompanyInfo)
  const [financialData, setFinancialData] =
    useState<CT600FinancialDataForm>(defaultFinancialData)
  const [balanceSheet, setBalanceSheet] =
    useState<CT600BalanceSheetForm>(defaultBalanceSheet)
  const [previousYear, setPreviousYear] =
    useState<CT600PreviousYearForm>(defaultPreviousYear)
  const [taxComp, setTaxComp] = useState<CT600TaxCompForm>(defaultTaxComp)
  const [supplementary, setSupplementary] =
    useState<CT600SupplementaryState>(defaultSupplementary)
  const [submission, setSubmission] =
    useState<CT600SubmissionForm>(defaultSubmission)
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({})

  // ---- Calculation & review ----
  const [calculationResult, setCalculationResult] =
    useState<GenericRecord | null>(null)
  const [calculationBreakdown, setCalculationBreakdown] =
    useState<Ct600CalculationBreakdown | null>(null)
  const [ct600Boxes, setCt600Boxes] = useState<GenericRecord | null>(null)
  const [isCalculating, setIsCalculating] = useState(false)
  const [isCheckingRequirements, setIsCheckingRequirements] = useState(false)

  // ---- Period split / LPOA ----
  const [periodSplit, setPeriodSplit] = useState<Ct600PeriodSplitData | null>(
    null
  )
  const [isLoadingPeriodSplit, setIsLoadingPeriodSplit] = useState(false)
  const [periodSplitError, setPeriodSplitError] = useState<string | null>(null)
  const periodSplitInFlightRef = useRef<string | null>(null)
  // Step 1 cannot advance until the user clicks "Confirm accounting period".
  // Reset whenever the company or period dates change so they re-confirm.
  const [periodConfirmed, setPeriodConfirmed] = useState(false)
  // Tracks whether the user has answered the "first Corporation Tax return?"
  // question so neither Yes nor No appears pre-selected until they choose.
  const [firstReturnAnswered, setFirstReturnAnswered] = useState(false)

  // ---- Per-period submission tracking ----
  const [periodSubmissions, setPeriodSubmissions] = useState<
    Ct600PeriodSubmission[]
  >([])
  const [aggregateStatus, setAggregateStatus] = useState<string | null>(null)
  const [isPollingStatus, setIsPollingStatus] = useState(false)

  // ---- Plan & payment ----
  const [selectedPlanSlug, setSelectedPlanSlug] = useState<string | null>(null)
  const [showPlanModal, setShowPlanModal] = useState(false)
  const {
    checkPayment,
    isPaid,
    showPaymentModal,
    setShowPaymentModal,
    currentFormId,
  } = useFormPayment()
  // The platform fee is a one-time payment per company per accounting period.
  // Coverage tells us whether this company+period is already paid for — even
  // via a *different* filing scope (e.g. they paid filing CT only and are now
  // back to file Company Accounts only for the same period).
  const [paymentCoverage, setPaymentCoverage] =
    useState<PaymentCoverage | null>(null)

  // ---- Loading / processing ----
  const [isLoading, setIsLoading] = useState(false)
  const [isInitializing, setIsInitializing] = useState(true)

  // ---- Submission ----
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showReviewPrompt, setShowReviewPrompt] = useState(false)
  const [submissionResult, setSubmissionResult] = useState<{
    status: string
    correlationId?: string
    message?: string
  } | null>(null)
  // Amending a return HMRC has already accepted reopens the wizard for edits.
  const [isAmending, setIsAmending] = useState(false)
  useEffect(() => setIsAmending(false), [formIdFromUrl])

  // ---- Form readiness (fetched on step 7) ----
  const [formReadiness, setFormReadiness] = useState<{
    readyForSubmission: boolean
    validationErrors: string[]
    blockers: string[]
  }>({ readyForSubmission: true, validationErrors: [], blockers: [] })

  // ---- Derived ----
  // Profit per accounts, the starting point of the tax computation. A dormant
  // company has no profit or loss, whatever the P&L inputs hold.
  const profitBeforeTax = useMemo(() => {
    if (companyInfo.flags.isDormant) return 0
    const p = (v: string) => parseFloat(v) || 0
    const totalIncome = p(financialData.turnover) + p(financialData.otherIncome)
    const grossProfit = totalIncome - p(financialData.costOfSales)
    const operatingProfit =
      grossProfit -
      p(financialData.distributionCosts) -
      p(financialData.administrativeExpenses)
    return (
      operatingProfit +
      p(financialData.interestReceivable) -
      p(financialData.interestPayable)
    )
  }, [financialData, companyInfo.flags.isDormant])

  // ========================================================
  // Period split / LPOA detection
  // ========================================================
  const fetchPeriodSplit = useCallback(
    async (
      companyNumber: string,
      options: {
        start?: string
        end?: string
        forceRefresh?: boolean
      } = {}
    ) => {
      if (!companyNumber) return
      // Avoid concurrent duplicate fetches for the same company+date pair
      const fetchKey = `${companyNumber}|${options.start ?? ''}|${options.end ?? ''}`
      if (periodSplitInFlightRef.current === fetchKey) return
      periodSplitInFlightRef.current = fetchKey

      setIsLoadingPeriodSplit(true)
      setPeriodSplitError(null)
      try {
        const response = await ct600Service.getPeriodSplit(
          companyNumber,
          options
        )
        const { data } = unwrap<Ct600PeriodSplitData>(response.response)
        if (!data || !data.periodOfAccount) {
          setPeriodSplitError('No period data returned.')
          return
        }
        setPeriodSplit(data)

        // Auto-fill the wizard's period dates if they're blank or
        // we used CH-derived dates (no override). Don't overwrite
        // user-supplied custom dates.
        const usedOverride = Boolean(options.start && options.end)
        setSetup(prev => {
          const shouldFillStart =
            !prev.accountingPeriodStart || usedOverride === false
          const shouldFillEnd =
            !prev.accountingPeriodEnd || usedOverride === false
          return {
            ...prev,
            ...(shouldFillStart
              ? { accountingPeriodStart: data.periodOfAccount.start }
              : {}),
            ...(shouldFillEnd
              ? { accountingPeriodEnd: data.periodOfAccount.end }
              : {}),
          }
        })
      } catch (err) {
        const message = extractApiError(
          err,
          "Could not look up the company's accounting period."
        )
        setPeriodSplitError(message)
        setPeriodSplit(null)
      } finally {
        setIsLoadingPeriodSplit(false)
        periodSplitInFlightRef.current = null
      }
    },
    []
  )

  /**
   * Compute the period split from user-entered dates with NO Companies House
   * lookup. Triggered by the "Calculate split" button on the setup step when
   * filingScope === 'ct_only' (mapped from returnType === 'tax-only'). The
   * user is responsible for entering the dates HMRC has on file.
   */
  const handleCalculateManualSplit = useCallback(async () => {
    if (!setup.accountingPeriodStart || !setup.accountingPeriodEnd) {
      setPeriodSplitError('Enter both an accounting period start and end date.')
      return
    }
    setIsLoadingPeriodSplit(true)
    setPeriodSplitError(null)
    try {
      const response = await ct600Service.calculatePeriodSplit({
        start: setup.accountingPeriodStart,
        end: setup.accountingPeriodEnd,
        ...(setup.companyNumber ? { companyNumber: setup.companyNumber } : {}),
        ...(setup.utr ? { utr: setup.utr } : {}),
      })
      const { data } = unwrap<Ct600PeriodSplitData>(response.response)
      if (!data || !data.periodOfAccount) {
        setPeriodSplitError('No period data returned.')
        setPeriodSplit(null)
        return
      }
      setPeriodSplit(data)
    } catch (err) {
      const message = extractApiError(err, 'Could not calculate period split.')
      setPeriodSplitError(message)
      setPeriodSplit(null)
    } finally {
      setIsLoadingPeriodSplit(false)
    }
  }, [
    setup.accountingPeriodStart,
    setup.accountingPeriodEnd,
    setup.companyNumber,
    setup.utr,
  ])

  // Re-run period split with override when user manually changes dates.
  // SKIPPED for `tax-only` (ct_only) — that flow is fully manual; the user
  // triggers the split via the "Calculate split" button so we don't second-
  // guess the dates they entered.
  useEffect(() => {
    if (setup.returnType === 'tax-only') return
    if (!setup.companyNumber) return
    if (!setup.accountingPeriodStart || !setup.accountingPeriodEnd) return
    if (!periodSplit) return
    // Only re-fetch if user dates differ from CH-derived ones
    if (
      setup.accountingPeriodStart === periodSplit.periodOfAccount.start &&
      setup.accountingPeriodEnd === periodSplit.periodOfAccount.end
    )
      return

    const id = setTimeout(() => {
      fetchPeriodSplit(setup.companyNumber, {
        start: setup.accountingPeriodStart,
        end: setup.accountingPeriodEnd,
      })
    }, 600)
    return () => clearTimeout(id)
  }, [
    setup.returnType,
    setup.companyNumber,
    setup.accountingPeriodStart,
    setup.accountingPeriodEnd,
    periodSplit,
    fetchPeriodSplit,
  ])

  // ========================================================
  // Load companies on mount
  // ========================================================
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setIsLoadingCompanies(true)
        const { response } = await companyService.getAllCompanies(1, 50)
        const responseData = response as any
        const actualData =
          responseData?.response?.data ?? responseData?.data ?? responseData
        let companiesData: StoredCompany[] = []

        if (Array.isArray(actualData)) {
          companiesData = actualData
        } else if (actualData && typeof actualData === 'object') {
          companiesData =
            actualData.companies ??
            actualData.data ??
            actualData.docs ??
            actualData.items ??
            []
        }

        const normalized = companiesData.map(c => ({
          ...c,
          companyName:
            c.companyName || c.name || `Company ${c.companyNumber || c._id}`,
        }))

        setCompanies(normalized)
      } catch (err) {
        console.error('Failed to load companies', err)
        if (err instanceof ApiError && err.status === 401) {
          toast.error('Authorization required. Please log in again.')
        } else {
          toast.error('Unable to fetch companies.')
        }
      } finally {
        setIsLoadingCompanies(false)
        setIsInitializing(false)
      }
    }

    loadCompanies()
  }, [])

  // Re-link the selected company after a reload. The form's companyId comes
  // back from the API, but `selectedCompany` only ever got set by clicking a
  // company on step 1 — so on a refresh the sidebar and setup step showed no
  // company at all until one was picked again.
  useEffect(() => {
    if (selectedCompany) return
    if (companies.length === 0) return
    if (!setup.companyId && !setup.companyNumber) return
    const match = companies.find(
      c =>
        (setup.companyId && c._id === setup.companyId) ||
        (setup.companyNumber && c.companyNumber === setup.companyNumber)
    )
    if (match) setSelectedCompany(match)
  }, [companies, selectedCompany, setup.companyId, setup.companyNumber])

  // ========================================================
  // Load existing form from URL
  // ========================================================
  useEffect(() => {
    if (!formIdFromUrl) return
    let active = true

    const loadForm = async () => {
      try {
        setIsLoading(true)
        const formResponse = await ct600Service.getForm(formIdFromUrl)
        if (!active) return

        const responseData: any = formResponse.response
        const form = (
          responseData?.success && responseData?.data
            ? responseData.data
            : responseData
        ) as Ct600Form

        setFormId(formIdFromUrl)
        setFormStatus(form.status as Ct600Status)

        // Populate setup from existing form
        setSetup(prev => ({
          ...prev,
          companyId:
            typeof form.companyId === 'string'
              ? form.companyId
              : (form.companyId as any)?._id || '',
          companyNumber: String(form.companyNumber || prev.companyNumber || ''),
          companyName: (form as any).companyName || prev.companyName,
          // The backend stores these as Dates, so they come back as full ISO
          // timestamps. `<input type="date">` silently renders anything that
          // isn't exactly YYYY-MM-DD as blank, so trim before storing.
          accountingPeriodStart: toDateInput(form.accountingPeriodStart),
          accountingPeriodEnd: toDateInput(form.accountingPeriodEnd),
          utr: form.utr || '',
          // The setup dropdown reflects the filing *scope*, so restore it from
          // the stored filingScope (not the backend returnType variant enum).
          returnType: fromFilingScope(
            (form as any).filingScope ??
              (form as any).sections?.companyInformation?.details?.filingScope
          ),
        }))

        // Read stored plan slug
        const storedPlan = readStoredPlanSlug(formIdFromUrl)
        if (storedPlan) setSelectedPlanSlug(storedPlan)

        // Populate sections from existing form data
        const sections = (form as any).sections || {}
        if (sections.companyInformation) {
          const ci = sections.companyInformation
          setCompanyInfo(prev => ({
            ...prev,
            details: {
              ...prev.details,
              companyName:
                ci.details?.companyName ??
                ci.companyName ??
                prev.details.companyName,
              companyRegistrationNumber:
                ci.details?.companyRegistrationNumber ??
                ci.companyRegistrationNumber ??
                prev.details.companyRegistrationNumber,
              utr: ci.details?.utr ?? ci.utr ?? prev.details.utr,
              tradingName:
                ci.details?.tradingName ??
                ci.tradingName ??
                prev.details.tradingName,
              returnType:
                ci.details?.returnType ??
                ci.returnType ??
                prev.details.returnType,
              firstReturn:
                ci.details?.firstReturn ??
                ci.firstReturn ??
                prev.details.firstReturn,
              finalReturn:
                ci.details?.finalReturn ??
                ci.finalReturn ??
                prev.details.finalReturn,
              amendedReturn:
                ci.details?.amendedReturn ??
                ci.amendedReturn ??
                prev.details.amendedReturn,
              accountingPeriod:
                ci.details?.accountingPeriod ??
                ci.accountingPeriod ??
                prev.details.accountingPeriod,
            },
            flags: {
              ...prev.flags,
              ...ci.flags,
            },
            contact: {
              ...prev.contact,
              ...ci.contact,
            },
            registeredOffice: {
              ...prev.registeredOffice,
              addressLine1:
                ci.registeredOffice?.addressLine1 ||
                ci.registeredOffice?.line1 ||
                prev.registeredOffice.addressLine1,
              addressLine2:
                ci.registeredOffice?.addressLine2 ||
                ci.registeredOffice?.line2 ||
                prev.registeredOffice.addressLine2,
              town:
                ci.registeredOffice?.town ||
                ci.registeredOffice?.city ||
                prev.registeredOffice.town,
              county:
                ci.registeredOffice?.county || prev.registeredOffice.county,
              postCode:
                ci.registeredOffice?.postCode ||
                ci.registeredOffice?.postcode ||
                prev.registeredOffice.postCode,
              country:
                ci.registeredOffice?.country || prev.registeredOffice.country,
            },
            ixbrlFiling: {
              averageEmployees:
                ci.ixbrlFiling?.averageEmployees != null
                  ? String(ci.ixbrlFiling.averageEmployees)
                  : prev.ixbrlFiling.averageEmployees,
            },
          }))
        }
        if (sections.financialData) {
          const fd = sections.financialData.statement ?? sections.financialData
          setFinancialData(prev => ({
            ...prev,
            turnover: String(fd.turnover ?? ''),
            otherIncome: String(fd.otherIncome ?? ''),
            costOfSales: String(fd.costOfSales ?? ''),
            distributionCosts: String(fd.distributionCosts ?? ''),
            administrativeExpenses: String(fd.administrativeExpenses ?? ''),
            interestReceivable: String(fd.interestReceivable ?? ''),
            interestPayable: String(fd.interestPayable ?? ''),
            taxExpense: String(fd.taxExpense ?? ''),
          }))
        }
        if (sections.balanceSheet) {
          const bs = sections.balanceSheet.statement ?? sections.balanceSheet
          // If the backend only stored the closing P&L Reserve and not the
          // opening balance, derive opening = closing - profit-after-tax so
          // the Financials step round-trips correctly.
          const fd =
            sections.financialData?.statement ?? sections.financialData ?? {}
          const num = (v: unknown) => parseFloat(String(v ?? '0')) || 0
          const profitAfterTax =
            num(fd.turnover) +
            num(fd.otherIncome) -
            num(fd.costOfSales) -
            num(fd.administrativeExpenses) -
            num(fd.distributionCosts) +
            num(fd.interestReceivable) -
            num(fd.interestPayable) -
            num(fd.taxExpense)
          const openingFromBackend =
            bs.openingProfitLoss !== undefined
              ? String(bs.openingProfitLoss)
              : (num(bs.profitAndLossAccount) - profitAfterTax).toFixed(2)
          setBalanceSheet(prev => ({
            ...prev,
            intangibleAssets: String(bs.intangibleAssets ?? ''),
            tangibleAssets: String(bs.tangibleAssets ?? ''),
            stocks: String(bs.stocks ?? ''),
            debtors: String(bs.debtors ?? ''),
            cashAtBankAndInHand: String(bs.cashAtBankAndInHand ?? ''),
            creditorsDueWithinOneYear: String(
              bs.creditorsDueWithinOneYear ?? ''
            ),
            creditorsDueAfterOneYear: String(bs.creditorsDueAfterOneYear ?? ''),
            calledUpShareCapital: String(bs.calledUpShareCapital ?? ''),
            openingProfitLoss: openingFromBackend,
            profitAndLossAccount: String(bs.profitAndLossAccount ?? ''),
          }))
        }
        // Previous-year comparative figures (summary subset).
        if (sections.previous) {
          const prev = sections.previous
          const prevFd =
            prev.financialData?.statement ?? prev.financialData ?? {}
          const prevBs = prev.balanceSheet?.statement ?? prev.balanceSheet ?? {}
          setPreviousYear({
            financialData: {
              turnover: String(prevFd.turnover ?? ''),
              otherIncome: String(prevFd.otherIncome ?? ''),
              costOfSales: String(prevFd.costOfSales ?? ''),
              distributionCosts: String(prevFd.distributionCosts ?? ''),
              administrativeExpenses: String(
                prevFd.administrativeExpenses ?? ''
              ),
              interestReceivable: String(prevFd.interestReceivable ?? ''),
              interestPayable: String(prevFd.interestPayable ?? ''),
              profitBeforeTax: String(prevFd.profitBeforeTax ?? ''),
              ukTaxOnProfits: String(prevFd.ukTaxOnProfits ?? ''),
            },
            balanceSheet: {
              intangibleAssets: String(prevBs.intangibleAssets ?? ''),
              tangibleAssets: String(prevBs.tangibleAssets ?? ''),
              stocks: String(prevBs.stocks ?? ''),
              debtors: String(prevBs.debtors ?? ''),
              cashAtBankAndInHand: String(prevBs.cashAtBankAndInHand ?? ''),
              creditorsDueAfterOneYear: String(
                prevBs.creditorsDueAfterOneYear ?? ''
              ),
              openingProfitLoss: String(prevBs.openingProfitLoss ?? ''),
              calledUpShareCapital: String(prevBs.calledUpShareCapital ?? ''),
              creditorsDueWithinOneYear: String(
                prevBs.creditorsDueWithinOneYear ?? ''
              ),
              netCurrentAssets: String(prevBs.netCurrentAssets ?? ''),
              totalAssetsLessCurrentLiabilities: String(
                prevBs.totalAssetsLessCurrentLiabilities ?? ''
              ),
              netAssets: String(prevBs.netAssets ?? ''),
              shareholdersFunds: String(prevBs.shareholdersFunds ?? ''),
              profitAndLossAccount: String(prevBs.profitAndLossAccount ?? ''),
            },
          })
        }
        if (sections.taxComputation) {
          const tc =
            sections.taxComputation.computation ?? sections.taxComputation
          setTaxComp(prev => ({
            ...prev,
            profitLossPerAccounts: String(tc.profitLossPerAccounts ?? ''),
            depreciation: String(tc.depreciation ?? ''),
            entertainmentDisallowable: String(
              tc.entertainmentDisallowable ?? ''
            ),
            otherAddBacks: String(tc.otherAddBacks ?? ''),
            capitalAllowances: String(tc.capitalAllowances ?? ''),
            bankInterest: String(tc.bankInterest ?? ''),
            lossesBroughtForward: String(tc.lossesBroughtForward ?? ''),
            charitableDonations: String(tc.charitableDonations ?? ''),
            otherDeductions: String(tc.otherDeductions ?? ''),
          }))
        }
        if (sections.supplementarySchedules) {
          const ss =
            sections.supplementarySchedules.schedules ||
            sections.supplementarySchedules
          // `wizardInput` holds the raw entries exactly as typed on step 5.
          // The supplementary-page models store the full HMRC shapes, which
          // don't map back onto this summary form, so the wizard reads its
          // own copy.
          const wi = sections.supplementarySchedules.wizardInput
          setSupplementary(prev => ({
            ...prev,
            requirements: {
              ct600a: wi?.requirements?.ct600a ?? ss.ct600a ?? prev.requirements.ct600a,
              ct600c: wi?.requirements?.ct600c ?? ss.ct600c ?? prev.requirements.ct600c,
              ct600l: wi?.requirements?.ct600l ?? ss.ct600l ?? prev.requirements.ct600l,
            },
            ct600a: wi?.ct600a
              ? { ...prev.ct600a, ...wi.ct600a }
              : prev.ct600a,
            ct600c: wi?.ct600c
              ? { ...prev.ct600c, ...wi.ct600c }
              : prev.ct600c,
            ct600l: wi?.ct600l
              ? { ...prev.ct600l, ...wi.ct600l }
              : prev.ct600l,
          }))
        }
        if (sections.declaration?.declaration) {
          const decl = sections.declaration.declaration
          setSubmission(prev => ({
            ...prev,
            submittedBy: decl.submittedBy || prev.submittedBy,
            submitterRole: decl.submitterRole || prev.submitterRole,
            declarationAccepted:
              decl.declarationAccepted ?? prev.declarationAccepted,
            agentReference: decl.agentReference || prev.agentReference,
          }))
        }

        // Restore the calculation only if tax was genuinely calculated. The
        // section is created zero-filled, so its mere presence would restore a
        // £0 "result" for a form nobody has calculated yet.
        const liabilities = sections.taxCalculation?.liabilities
        const wasCalculated =
          hasFigures(liabilities) ||
          (Array.isArray(liabilities?.financialYears) &&
            liabilities.financialYears.length > 0)
        if (wasCalculated) {
          setCalculationResult(sections.taxCalculation)
          // Also fetch CT600 boxes in background
          ct600Service
            .getCt600Boxes(formIdFromUrl)
            .then(boxesResponse => {
              try {
                const boxesData = unwrap<any>(boxesResponse.response)
                setCt600Boxes(boxesData.data?.boxes || null)
              } catch {
                /* boxes optional */
              }
            })
            .catch(() => {
              /* boxes optional */
            })
        }

        // Restore breakdown if backend stored it on the form
        const storedBreakdown =
          sections.taxCalculation?.breakdown ??
          (form as any).breakdown ??
          sections.breakdown
        if (storedBreakdown && Array.isArray(storedBreakdown.periods)) {
          setCalculationBreakdown(storedBreakdown as Ct600CalculationBreakdown)
        }

        // Restore per-period submissions if present (LPOA tracking)
        if (
          Array.isArray(form.periodSubmissions) &&
          form.periodSubmissions.length > 0
        ) {
          setPeriodSubmissions(
            form.periodSubmissions as Ct600PeriodSubmission[]
          )
        }

        // Auto-fetch period split for existing forms so the LPOA banner reflects state
        if (
          form.companyNumber &&
          form.accountingPeriodStart &&
          form.accountingPeriodEnd
        ) {
          void fetchPeriodSplit(String(form.companyNumber), {
            start: String(form.accountingPeriodStart).slice(0, 10),
            end: String(form.accountingPeriodEnd).slice(0, 10),
          })
          // An existing form already has a period on file — treat it as
          // confirmed so the user isn't blocked when resuming.
          setPeriodConfirmed(true)
          setFirstReturnAnswered(true)
        } else if (form.companyNumber) {
          void fetchPeriodSplit(String(form.companyNumber))
        }

        // Determine completed steps from the data each section actually
        // carries. A new form is created with EVERY section already present and
        // zero-filled, so testing `if (sections.x)` marked all seven steps
        // complete the moment step 1 was saved.
        const completed = new Set<CT600WizardStepId>()
        // Step 1 is done once the form exists with a company and a period —
        // the backend requires both to create it.
        if (form.companyNumber && form.accountingPeriodStart) completed.add(1)
        // Step 2 mirrors its own validation: contact name and phone.
        const ci = sections.companyInformation
        if (ci?.contact?.name && ci?.contact?.phone) completed.add(2)
        // Step 3 needs at least one real figure in either statement.
        if (
          hasFigures(sections.financialData?.statement) ||
          hasFigures(sections.balanceSheet?.statement)
        )
          completed.add(3)
        if (hasFigures(sections.taxComputation?.computation)) completed.add(4)
        // Step 5 writes `wizardInput` whenever it is saved — including when
        // no supplementary pages apply, which is itself a completed answer.
        if (sections.supplementarySchedules?.wizardInput) completed.add(5)
        // Step 6 is only done once tax has actually been calculated — the same
        // signal used to restore the calculation above.
        if (wasCalculated) completed.add(6)
        if (sections.declaration?.declaration?.declarationAccepted)
          completed.add(7)

        // For submitted/accepted forms, mark all steps completed
        const status = form.status as string
        if (
          status === 'submitted' ||
          status === 'accepted' ||
          status === 'rejected'
        ) {
          for (let i = 1; i <= 7; i++) completed.add(i as CT600WizardStepId)
        }

        // If we just created the form in handleNext, skip overriding wizard position —
        // handleNext already advanced the step sequentially.
        if (justCreatedRef.current) {
          justCreatedRef.current = false
        } else {
          setCompletedSteps(completed)

          // Navigate to the furthest incomplete step, or last step if all done
          const allSteps: CT600WizardStepId[] = [1, 2, 3, 4, 5, 6, 7]
          const firstIncomplete = allSteps.find(s => !completed.has(s))
          setCurrentStep(firstIncomplete ?? 7)
        }
      } catch (err) {
        console.error('Failed to load form', err)
        toast.error('Failed to load the requested form.')
      } finally {
        if (active) setIsLoading(false)
      }
    }

    loadForm()
    return () => {
      active = false
    }
  }, [formIdFromUrl])

  // ========================================================
  // Keep the form id in the URL
  // ========================================================
  // A refresh can only restore the form if its id is in the address bar, so as
  // soon as we know one — created on step 1, resumed from a draft, or set by
  // any save — mirror it into the URL. `replace` keeps the Back button pointing
  // at wherever the user came from rather than at an idless copy of this page.
  useEffect(() => {
    if (!formId) return
    if (formIdFromUrl === formId) return
    navigate(`/ct600-filing/${formId}`, { replace: true })
  }, [formId, formIdFromUrl, navigate])

  // ========================================================
  // Draft persistence
  // ========================================================
  const saveDraft = useCallback(() => {
    if (!setup.companyNumber || !setup.accountingPeriodStart) return
    try {
      ct600DraftService.saveDraft({
        companyId: setup.companyId,
        companyNumber: setup.companyNumber,
        companyName: setup.companyName,
        formId,
        currentStep,
        planSlug: selectedPlanSlug || '',
        savedAt: new Date().toISOString(),
        setup,
        companyInfo,
        financialData,
        balanceSheet,
        previousYear,
        taxComputation: taxComp,
        supplementary,
        submission,
      })
    } catch (e) {
      console.warn('Auto-save draft failed:', e)
    }
  }, [
    setup,
    companyInfo,
    financialData,
    balanceSheet,
    previousYear,
    taxComp,
    supplementary,
    submission,
    formId,
    currentStep,
    selectedPlanSlug,
  ])

  // ========================================================
  // Company selection handler
  // ========================================================
  const handleCompanySelect = (company: StoredCompany) => {
    setSelectedCompany(company)
    // Reset prior period split when switching company
    setPeriodSplit(null)
    setPeriodSplitError(null)
    setPeriodConfirmed(false)
    setSetup(prev => ({
      ...prev,
      companyId: company._id,
      companyNumber: company.companyNumber,
      companyName: company.companyName || company.name || '',
    }))
    // Auto-fetch from Companies House — UNLESS the user has chosen `tax-only`
    // (ct_only). In that case CH may have rolled to the next reference period
    // (accounts filed elsewhere) so an auto-fetch would prefill the wrong dates.
    // The user enters dates manually and triggers a split via the button.
    if (company.companyNumber && setup.returnType !== 'tax-only') {
      void fetchPeriodSplit(company.companyNumber)
    }
    setCompanyInfo(prev => ({
      ...prev,
      details: {
        ...prev.details,
        companyName: company.companyName || company.name || '',
        companyRegistrationNumber: company.companyNumber,
        utr: company.hmrcCredentials?.companyUtr || prev.details.utr,
      },
      registeredOffice: {
        ...prev.registeredOffice,
        addressLine1:
          company.registeredOffice?.addressLine1 ||
          company.registeredOffice?.line1 ||
          '',
        addressLine2:
          company.registeredOffice?.addressLine2 ||
          company.registeredOffice?.line2 ||
          '',
        town:
          company.registeredOffice?.locality ||
          company.registeredOffice?.city ||
          '',
        county:
          company.registeredOffice?.region ||
          company.registeredOffice?.county ||
          '',
        postCode:
          company.registeredOffice?.postalCode ||
          company.registeredOffice?.postcode ||
          '',
        country: company.registeredOffice?.country || 'United Kingdom',
      },
    }))

    // Check for existing draft
    if (
      company.companyNumber &&
      setup.accountingPeriodStart &&
      !draftLoadedRef.current
    ) {
      const draft = ct600DraftService.loadDraftByIdentifiers(
        company.companyNumber,
        setup.accountingPeriodStart
      )
      if (draft) {
        draftLoadedRef.current = true
        // Resuming a draft with an existing period — treat as confirmed.
        setPeriodConfirmed(true)
        setFirstReturnAnswered(true)
        setCompanyInfo(draft.companyInfo)
        setFinancialData(draft.financialData)
        setBalanceSheet(draft.balanceSheet)
        if (draft.previousYear) setPreviousYear(draft.previousYear)
        setTaxComp(draft.taxComputation)
        setSupplementary(draft.supplementary)
        setSubmission(draft.submission)
        if (draft.formId) setFormId(draft.formId)
        if (draft.currentStep > 1) {
          setCurrentStep(draft.currentStep)
          const completed = new Set<CT600WizardStepId>()
          for (let i = 1; i < draft.currentStep; i++)
            completed.add(i as CT600WizardStepId)
          setCompletedSteps(completed)
        }
        if (draft.planSlug) setSelectedPlanSlug(draft.planSlug)
        toast.success('Draft restored from previous session.')
      }
    }
  }

  // ========================================================
  // Validation
  // ========================================================
  const validateStep = (step: CT600WizardStepId): boolean => {
    const errors: Record<string, string> = {}

    if (step === 1) {
      if (!setup.companyId) errors.companyId = 'Please select a company.'
      if (!setup.accountingPeriodStart)
        errors.periodStart = 'Period start is required.'
      if (!setup.accountingPeriodEnd)
        errors.periodEnd = 'Period end is required.'
      // UTR is only required when Corporation Tax is part of the filing.
      // Company-accounts-only filings don't need it.
      if (
        setup.returnType !== 'accounts-only' &&
        (!setup.utr || setup.utr.length !== 10)
      )
        errors.utr = 'A valid 10-digit UTR is required.'
      // A plan is required UNLESS the company is already covered (one-off
      // payment for this company is still inside its 12-month window). The
      // backend will accept the submission without a plan in that case.
      if (!selectedPlanSlug && !formId && !isCovered)
        errors.planSlug = 'Please select a pricing plan.'
      // For ct_only, the user enters dates manually so there's no Companies House
      // auto-fetch to confirm them. Require an explicit "Calculate split" click
      // (which populates `periodSplit`) before letting them advance — otherwise
      // they could submit dates that don't match what HMRC has on file.
      if (
        setup.returnType === 'tax-only' &&
        setup.accountingPeriodStart &&
        setup.accountingPeriodEnd &&
        !periodSplit
      ) {
        errors.periodSplit =
          "Click 'Calculate split' to confirm your accounting period."
      }
    }

    if (step === 2) {
      if (!companyInfo.contact.name.trim())
        errors.contactName = 'Contact name is required.'
      if (!companyInfo.contact.phone.trim())
        errors.contactPhone = 'Contact phone is required.'
    }

    if (step === 3) {
      // Critical balance-sheet integrity check (per accounts spec):
      // Net Assets MUST equal Total Shareholders' Funds. Block progression
      // until the user fixes the difference.
      const num = (v: string) => parseFloat(v) || 0
      const totalAssets =
        num(balanceSheet.intangibleAssets) +
        num(balanceSheet.tangibleAssets) +
        num(balanceSheet.stocks) +
        num(balanceSheet.debtors) +
        num(balanceSheet.cashAtBankAndInHand)
      const totalLiabilities =
        num(balanceSheet.creditorsDueWithinOneYear) +
        num(balanceSheet.creditorsDueAfterOneYear)
      const netAssets = totalAssets - totalLiabilities

      const totalIncome =
        num(financialData.turnover) + num(financialData.otherIncome)
      const grossProfit = totalIncome - num(financialData.costOfSales)
      const operatingProfit =
        grossProfit -
        num(financialData.administrativeExpenses) -
        num(financialData.distributionCosts)
      const profitBeforeTax =
        operatingProfit +
        num(financialData.interestReceivable) -
        num(financialData.interestPayable)
      const profitAfterTax = profitBeforeTax - num(financialData.taxExpense)

      const shareholdersFunds =
        num(balanceSheet.calledUpShareCapital) +
        num(balanceSheet.openingProfitLoss) +
        profitAfterTax

      if (Math.abs(netAssets - shareholdersFunds) >= 0.005) {
        errors.balanceSheet = `Balance sheet does not balance. Net Assets (£${netAssets.toFixed(2)}) must equal Total Shareholders' Funds (£${shareholdersFunds.toFixed(2)}).`
      }
    }

    setValidationErrors(errors)
    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0]
      toast.error(firstError || 'Please complete all required fields.')
      return false
    }
    return true
  }

  // ========================================================
  // Section patching
  // ========================================================
  const patchSection = async (
    section: string,
    data: Record<string, unknown>
  ) => {
    if (!formId) return
    try {
      await ct600Service.patchSection(formId, section, {
        [section]: data,
      } as any)
    } catch (err) {
      console.error(`Failed to patch section ${section}:`, err)
      toast.error(extractApiError(err, `Failed to save ${section}.`))
      throw err
    }
  }

  // ========================================================
  // Step advancement
  // ========================================================
  // A loaded form can carry a partial/stale calculationResult (e.g. only
  // taxableProfit, no corporationTax). Treat the calculation as valid only
  // when a real numeric corporation-tax figure is present — either the flat
  // result (single 12-month period) or the breakdown total (LPOA).
  const hasValidCalculation =
    (calculationResult != null &&
      typeof calculationResult.corporationTax === 'number') ||
    (calculationBreakdown != null &&
      typeof calculationBreakdown.totalCorporationTax === 'number')

  /**
   * Push everything captured on `step` to the backend.
   *
   * Extracted from handleNext so that "Save & Close" and "Previous" persist the
   * step in progress as well. Previously only "Next" wrote to the API, so a
   * user who typed into a step and then saved/closed (or refreshed) got the
   * form back empty — the reload reads exclusively from the backend, and the
   * localStorage draft is only offered on step 1 of a form with no company yet.
   */
  const persistStep = async (
    step: CT600WizardStepId,
    options: { silent?: boolean } = {}
  ) => {
    const notify = (message: string) => {
      if (!options.silent) toast.success(message)
    }

    // Step 1: Create the form, or push edited period/UTR onto an existing one
    if (step === 1) {
      if (!formId) {
        // Nothing to create until a company and a period are chosen.
        if (
          !setup.companyId ||
          !setup.accountingPeriodStart ||
          !setup.accountingPeriodEnd
        )
          return

        const payload: any = {
          companyId: setup.companyId,
          accountingPeriodStart: setup.accountingPeriodStart,
          accountingPeriodEnd: setup.accountingPeriodEnd,
          utr: setup.utr,
          // `returnType` is the backend CT600 variant enum (full/short/
          // amended/dormant) derived from flags — NOT the scope dropdown.
          // The scope dropdown maps to `filingScope`.
          returnType: toReturnType({
            isDormant: companyInfo.flags.isDormant,
            amendedReturn: companyInfo.details.amendedReturn,
          }),
          filingScope: toFilingScope(setup.returnType),
          planSlug: selectedPlanSlug || '',
        }

        const response = await ct600Service.createForm(payload)
        const { data } = unwrap<any>(response.response)
        const newFormId = data?._id || data?.id || data?.formId
        if (newFormId) {
          setFormId(newFormId)
          setFormStatus('draft')
          if (selectedPlanSlug) persistPlanSlug(newFormId, selectedPlanSlug)

          // Update URL — mark as just-created so loadForm skips step navigation
          justCreatedRef.current = true
          navigate(`/ct600-filing/${newFormId}`, { replace: true })
          notify('CT600 form created successfully.')
        }
      } else if (setup.accountingPeriodStart && setup.accountingPeriodEnd) {
        // The form already exists — the period or UTR may have been edited on
        // the way back through step 1, so mirror it to the backend.
        await ct600Service.updateForm(formId, {
          accountingPeriodStart: setup.accountingPeriodStart,
          accountingPeriodEnd: setup.accountingPeriodEnd,
          ...(setup.utr ? { utr: setup.utr } : {}),
        })
      }
    }

    // Step 2: Patch company information
    if (step === 2 && formId) {
      const averageEmployeesRaw = companyInfo.ixbrlFiling.averageEmployees
      const averageEmployees =
        averageEmployeesRaw === '' ? undefined : parseInt(averageEmployeesRaw, 10)
      await patchSection('companyInformation', {
        details: {
          ...companyInfo.details,
          returnType: toReturnType({
            isDormant: companyInfo.flags.isDormant,
            amendedReturn: companyInfo.details.amendedReturn,
          }),
          filingScope: toFilingScope(setup.returnType),
          accountingPeriod: {
            start: setup.accountingPeriodStart,
            end: setup.accountingPeriodEnd,
          },
        },
        flags: companyInfo.flags,
        contact: companyInfo.contact,
        registeredOffice: companyInfo.registeredOffice,
        ...(averageEmployees !== undefined && !Number.isNaN(averageEmployees)
          ? { ixbrlFiling: { averageEmployees } }
          : {}),
      })
      notify('Company information saved.')
    }

    // Step 3: Patch financial data and balance sheet
    if (step === 3 && formId) {
      const parseNum = (v: string) => parseFloat(v) || 0
      // Profit After Tax (computed) flows into the closing P&L Reserve.
      const profitAfterTaxNum =
        parseNum(financialData.turnover) +
        parseNum(financialData.otherIncome) -
        parseNum(financialData.costOfSales) -
        parseNum(financialData.administrativeExpenses) -
        parseNum(financialData.distributionCosts) +
        parseNum(financialData.interestReceivable) -
        parseNum(financialData.interestPayable) -
        parseNum(financialData.taxExpense)
      const closingPnl =
        parseNum(balanceSheet.openingProfitLoss) + profitAfterTaxNum

      await patchSection('financialData', {
        statement: {
          turnover: parseNum(financialData.turnover),
          otherIncome: parseNum(financialData.otherIncome),
          costOfSales: parseNum(financialData.costOfSales),
          distributionCosts: parseNum(financialData.distributionCosts),
          administrativeExpenses: parseNum(
            financialData.administrativeExpenses
          ),
          interestReceivable: parseNum(financialData.interestReceivable),
          interestPayable: parseNum(financialData.interestPayable),
          taxExpense: parseNum(financialData.taxExpense),
          profitAfterTax: profitAfterTaxNum,
        },
      })
      await patchSection('balanceSheet', {
        statement: {
          intangibleAssets: parseNum(balanceSheet.intangibleAssets),
          tangibleAssets: parseNum(balanceSheet.tangibleAssets),
          stocks: parseNum(balanceSheet.stocks),
          debtors: parseNum(balanceSheet.debtors),
          cashAtBankAndInHand: parseNum(balanceSheet.cashAtBankAndInHand),
          creditorsDueWithinOneYear: parseNum(
            balanceSheet.creditorsDueWithinOneYear
          ),
          creditorsDueAfterOneYear: parseNum(
            balanceSheet.creditorsDueAfterOneYear
          ),
          calledUpShareCapital: parseNum(balanceSheet.calledUpShareCapital),
          // iXBRL `ProfitLossAccountReserve` (closing) — derived from the
          // user-entered opening balance plus this period's profit/loss.
          profitAndLossAccount: closingPnl,
          openingProfitLoss: parseNum(balanceSheet.openingProfitLoss),
        },
      })
      // Previous-year comparative figures live in their own section. Every
      // field the user can type has to go up, not just the summary totals —
      // the loader reads them all back, so anything omitted here reappears
      // blank after a refresh.
      const prevFd = previousYear.financialData
      const prevBs = previousYear.balanceSheet
      // Blank stays blank. Coercing an untouched comparative to 0 would make
      // every prior-year box read back as "0.00" after a reload, and the
      // iXBRL prior-year column keys off which figures are actually numbers.
      // `null` (rather than omitting the key) means clearing a value sticks —
      // the backend merges section patches key by key.
      const numOrNull = (v: string) => (v === '' ? null : parseFloat(v) || 0)
      const hasPreviousData =
        Object.values(prevFd).some(v => v !== '') ||
        Object.values(prevBs).some(v => v !== '')
      if (hasPreviousData) {
        await patchSection('previous', {
          financialData: {
            statement: {
              turnover: numOrNull(prevFd.turnover),
              otherIncome: numOrNull(prevFd.otherIncome),
              costOfSales: numOrNull(prevFd.costOfSales),
              distributionCosts: numOrNull(prevFd.distributionCosts),
              administrativeExpenses: numOrNull(prevFd.administrativeExpenses),
              interestReceivable: numOrNull(prevFd.interestReceivable),
              interestPayable: numOrNull(prevFd.interestPayable),
              profitBeforeTax: numOrNull(prevFd.profitBeforeTax),
              ukTaxOnProfits: numOrNull(prevFd.ukTaxOnProfits),
            },
          },
          balanceSheet: {
            statement: {
              intangibleAssets: numOrNull(prevBs.intangibleAssets),
              tangibleAssets: numOrNull(prevBs.tangibleAssets),
              stocks: numOrNull(prevBs.stocks),
              debtors: numOrNull(prevBs.debtors),
              cashAtBankAndInHand: numOrNull(prevBs.cashAtBankAndInHand),
              creditorsDueAfterOneYear: numOrNull(
                prevBs.creditorsDueAfterOneYear
              ),
              openingProfitLoss: numOrNull(prevBs.openingProfitLoss),
              calledUpShareCapital: numOrNull(prevBs.calledUpShareCapital),
              creditorsDueWithinOneYear: numOrNull(
                prevBs.creditorsDueWithinOneYear
              ),
              netCurrentAssets: numOrNull(prevBs.netCurrentAssets),
              totalAssetsLessCurrentLiabilities: numOrNull(
                prevBs.totalAssetsLessCurrentLiabilities
              ),
              netAssets: numOrNull(prevBs.netAssets),
              shareholdersFunds: numOrNull(prevBs.shareholdersFunds),
              profitAndLossAccount: numOrNull(prevBs.profitAndLossAccount),
            },
          },
        })
      }
      notify('Financial statements saved.')
    }

    // Step 4: Patch tax computation
    if (step === 4 && formId) {
      const parseNum = (v: string) => parseFloat(v) || 0
      await patchSection('taxComputation', {
        computation: {
          // The P&L figure shown on the step, not the value loaded with the
          // form, which goes stale once the P&L changes.
          profitLossPerAccounts: profitBeforeTax,
          depreciation: parseNum(taxComp.depreciation),
          entertainmentDisallowable: parseNum(taxComp.entertainmentDisallowable),
          otherAddBacks: parseNum(taxComp.otherAddBacks),
          capitalAllowances: parseNum(taxComp.capitalAllowances),
          bankInterest: parseNum(taxComp.bankInterest),
          lossesBroughtForward: parseNum(taxComp.lossesBroughtForward),
          charitableDonations: parseNum(taxComp.charitableDonations),
          otherDeductions: parseNum(taxComp.otherDeductions),
        },
      })
      notify('Tax computation saved.')
    }

    // Step 5: Upsert supplementary pages
    if (step === 5 && formId) {
      // The wizard's supplementary inputs are a flat summary; the supplementary
      // page models are the full HMRC shapes. Store the raw entries on the
      // section as well so the wizard can show back exactly what was typed —
      // `wizardInput` is outside the section's canonical field list, so the
      // shape normaliser leaves it alone.
      await patchSection('supplementarySchedules', {
        wizardInput: {
          requirements: supplementary.requirements,
          ct600a: supplementary.ct600a,
          ct600c: supplementary.ct600c,
          ct600l: supplementary.ct600l,
        },
      })
      if (supplementary.requirements.ct600a) {
        // Every loan field is required by the CT600A model, so a half-filled
        // row would 422 the whole save. Incomplete rows still round-trip via
        // `wizardInput` above; they just don't reach the supplementary page
        // until the user completes them.
        const completeLoans = supplementary.ct600a.outstandingLoans.filter(
          l => l.participatorName && l.participatorAddress && l.dateOfLoan
        )
        await ct600Service.upsertSupplementaryPage(formId, 'ct600a', {
          part1: {
            hasLoansRepaidOrReleased: false,
            outstandingLoans: completeLoans.map(l => ({
              participatorName: l.participatorName,
              participatorAddress: l.participatorAddress,
              loanAmount: parseFloat(l.loanAmount) || 0,
              dateOfLoan: l.dateOfLoan,
            })),
            totalOutstandingLoans:
              parseFloat(supplementary.ct600a.totalOutstandingLoans) || 0,
            taxChargeable: parseFloat(supplementary.ct600a.taxChargeable) || 0,
          },
        })
      }
      if (supplementary.requirements.ct600c) {
        // Only the total is sent: a `part1.claims` entry also requires a
        // `lossType`, which this step doesn't ask for, and guessing one would
        // put an invented value into an HMRC filing. The claimant name/UTR are
        // kept in `wizardInput`.
        await ct600Service.upsertSupplementaryPage(formId, 'ct600c', {
          part1: {
            totalAmountClaimed:
              parseFloat(supplementary.ct600c.reliefAmount) || 0,
          },
        })
      }
      if (supplementary.requirements.ct600l) {
        // `rdEnhancement` has no slot in the CT600L model (it's an RDEC
        // step-by-step schedule) — it lives in `wizardInput` only.
        await ct600Service.upsertSupplementaryPage(formId, 'ct600l', {
          step1: {
            rdExpenditure: parseFloat(supplementary.ct600l.rdExpenditure) || 0,
            rdecClaim: parseFloat(supplementary.ct600l.rdCredit) || 0,
          },
        })
      }
      notify('Supplementary pages saved.')
    }

    // Step 7: Persist the declaration so it survives a reload. Government
    // Gateway credentials are deliberately excluded — they are only ever sent
    // as part of the submit call.
    if (step === 7 && formId) {
      await patchSection('declaration', {
        declaration: {
          submittedBy: submission.submittedBy,
          submitterRole: submission.submitterRole,
          declarationAccepted: submission.declarationAccepted,
          ...(submission.agentReference
            ? { agentReference: submission.agentReference }
            : {}),
        },
      })
    }
  }

  const handleNext = async () => {
    if (!validateStep(currentStep)) return

    // The "Confirm accounting period" step only applies to the ct_only flow
    // (the manual date-split). For "both" and "accounts only" the dates come
    // straight from Companies House, so there's nothing to confirm.
    if (
      currentStep === 1 &&
      setup.returnType === 'tax-only' &&
      !periodConfirmed
    ) {
      toast.error('Please click Confirm accounting period before proceeding.')
      return
    }

    if (currentStep === 6 && !hasValidCalculation) {
      toast.error('Please click Calculate Tax above before proceeding.')
      return
    }

    setIsLoading(true)

    try {
      await persistStep(currentStep)

      // Mark current step as completed and advance
      setCompletedSteps(prev => new Set([...prev, currentStep]))
      const nextStep = Math.min(currentStep + 1, 7) as CT600WizardStepId
      setCurrentStep(nextStep)

      // Auto-save draft
      saveDraft()
    } catch (err) {
      console.error('Step advancement failed:', err)
      toast.error(extractApiError(err, 'Failed to save. Please try again.'))
    } finally {
      setIsLoading(false)
    }
  }

  const handlePrevious = () => {
    if (currentStep > 1) {
      const leavingStep = currentStep
      setValidationErrors({})
      setCurrentStep((currentStep - 1) as CT600WizardStepId)
      saveDraft()
      // Best-effort: keep what was typed on the step being left. Stepping back
      // is not a validated action, so a rejected patch must not block it.
      void persistStep(leavingStep, { silent: true }).catch(err => {
        console.warn('Could not persist step before going back:', err)
      })
    }
  }

  const handleStepClick = (step: CT600WizardStepId) => {
    if (step === 7 && !hasValidCalculation) {
      toast.error(
        'Please click Calculate Tax on the Tax Computation step before proceeding.'
      )
      return
    }
    if (completedSteps.has(step) || step === currentStep) {
      const leavingStep = currentStep
      setValidationErrors({})
      setCurrentStep(step)
      saveDraft()
      if (leavingStep !== step) {
        // Jumping around the wizard shouldn't discard the step being left.
        void persistStep(leavingStep, { silent: true }).catch(err => {
          console.warn('Could not persist step before jumping:', err)
        })
      }
    }
  }

  // ========================================================
  // Calculate tax (Step 6)
  // ========================================================
  const handleCalculate = async () => {
    if (!formId) {
      toast.error('No form to calculate. Please complete previous steps.')
      return
    }

    setIsCalculating(true)
    try {
      const response = await ct600Service.calculate(formId)
      const { data } = unwrap<any>(response.response)
      const calcData = data || response.response

      // Backend now returns: { calculations, breakdown, form, ... }
      // Older shape was just the form. Normalize to a usable calculationResult
      // and extract breakdown separately.
      const calcSnapshot =
        calcData?.calculations ??
        calcData?.form?.sections?.taxCalculation ??
        calcData?.sections?.taxCalculation ??
        calcData
      setCalculationResult(calcSnapshot || null)

      const breakdown = calcData?.breakdown ?? null
      if (breakdown && Array.isArray(breakdown.periods)) {
        setCalculationBreakdown(breakdown as Ct600CalculationBreakdown)
      } else {
        setCalculationBreakdown(null)
      }

      // Also fetch boxes
      try {
        const boxesResponse = await ct600Service.getCt600Boxes(formId)
        const boxesData = unwrap<any>(boxesResponse.response)
        setCt600Boxes(boxesData.data?.boxes || null)
      } catch {
        /* boxes optional */
      }

      // Update status
      try {
        const statusResponse = await ct600Service.getFormStatus(formId)
        const statusData = unwrap<{ status: Ct600Status }>(
          statusResponse.response
        )
        setFormStatus(statusData.data?.status || null)
      } catch {
        /* status optional */
      }

      toast.success('Tax calculation completed.')
    } catch (err) {
      console.error('Calculation failed:', err)
      toast.error(extractApiError(err, 'Tax calculation failed.'))
    } finally {
      setIsCalculating(false)
    }
  }

  // ========================================================
  // Check supplementary requirements (Step 5)
  // ========================================================
  useEffect(() => {
    if (currentStep !== 5 || !formId) return
    let active = true

    const checkRequirements = async () => {
      setIsCheckingRequirements(true)
      try {
        const response =
          await ct600Service.checkSupplementaryRequirements(formId)
        if (!active) return
        const { data } = unwrap<any>(response.response)
        if (data?.requirements) {
          setSupplementary(prev => ({
            ...prev,
            requirements: {
              ct600a: data.requirements.ct600a ?? prev.requirements.ct600a,
              ct600c: data.requirements.ct600c ?? prev.requirements.ct600c,
              ct600l: data.requirements.ct600l ?? prev.requirements.ct600l,
            },
          }))
        }
      } catch {
        // Non-critical
      } finally {
        if (active) setIsCheckingRequirements(false)
      }
    }

    checkRequirements()
    return () => {
      active = false
    }
  }, [currentStep, formId])

  // ========================================================
  // Payment coverage (one-time fee per company + period)
  // ========================================================
  const refreshCoverage =
    useCallback(async (): Promise<PaymentCoverage | null> => {
      if (!setup.companyId) return null
      try {
        const coverage = await paymentService.getPaymentCoverage({
          companyId: setup.companyId,
          ...(setup.accountingPeriodStart
            ? { periodStart: setup.accountingPeriodStart }
            : {}),
          ...(setup.accountingPeriodEnd
            ? { periodEnd: setup.accountingPeriodEnd }
            : {}),
        })
        setPaymentCoverage(coverage)
        return coverage
      } catch {
        // Treat a failed coverage lookup as "no coverage" — the existing
        // per-form payment check still gates submission, so we fail safe.
        setPaymentCoverage(null)
        return null
      }
    }, [
      setup.companyId,
      setup.accountingPeriodStart,
      setup.accountingPeriodEnd,
    ])

  // Re-check coverage whenever the company or period is known/changes so the
  // submit step reflects an existing payment for the same company+period.
  useEffect(() => {
    if (!setup.companyId) return
    void refreshCoverage()
  }, [
    setup.companyId,
    setup.accountingPeriodStart,
    setup.accountingPeriodEnd,
    refreshCoverage,
  ])

  // True when this company+period is already paid for, by any filing scope.
  const isCovered = paymentCoverage?.paid === true

  // ========================================================
  // Check payment status when form loads and on Step 7
  // ========================================================
  useEffect(() => {
    if (!formId) return
    checkPayment(formId)
  }, [formId])

  useEffect(() => {
    if (currentStep !== 7 || !formId) return
    checkPayment(formId)

    // Fetch form readiness/validation status
    const fetchReadiness = async () => {
      try {
        const statusResponse = await ct600Service.getFormStatus(formId)
        const statusData = unwrap<any>(statusResponse.response)
        const data = statusData.data || statusResponse.response
        if (data?.status) setFormStatus(data.status)
        setFormReadiness({
          readyForSubmission: data?.readiness?.readyForSubmission ?? true,
          validationErrors: data?.validation?.errors ?? [],
          blockers: data?.readiness?.blockers ?? [],
        })
      } catch {
        /* non-critical */
      }
    }
    fetchReadiness()
  }, [currentStep, formId])

  // ========================================================
  // Poll HMRC submission status (per-period, LPOA-aware)
  // ========================================================
  const pollSubmissionStatus = useCallback(async () => {
    if (!formId) return
    setIsPollingStatus(true)
    try {
      const statusResponse = await ct600Service.getSubmissionStatus(formId)
      const { data } = unwrap<any>(statusResponse.response)

      const formData = data?.form
      if (formData?.status) {
        setFormStatus(formData.status)
        setAggregateStatus(formData.status)
      }

      const ps: Ct600PeriodSubmission[] = Array.isArray(
        formData?.periodSubmissions
      )
        ? formData.periodSubmissions
        : []
      if (ps.length > 0) {
        setPeriodSubmissions(ps)
      } else if (Array.isArray(data?.polled) && data.polled.length > 0) {
        // Legacy fallback: synthesize period submissions from `polled[]`
        setPeriodSubmissions(prev => {
          const byIndex = new Map<number, Ct600PeriodSubmission>()
          for (const p of prev) byIndex.set(p.periodIndex, p)
          for (const p of data.polled as any[]) {
            const existing = byIndex.get(p.periodIndex) ?? {
              periodIndex: p.periodIndex,
            }
            byIndex.set(p.periodIndex, {
              ...existing,
              periodIndex: p.periodIndex,
              ...(p.correlationId ? { correlationId: p.correlationId } : {}),
              ...(p.status ? { status: p.status } : {}),
              ...(typeof p.requiresPolling === 'boolean'
                ? { requiresPolling: p.requiresPolling }
                : {}),
            } as Ct600PeriodSubmission)
          }
          return Array.from(byIndex.values()).sort(
            (a, b) => a.periodIndex - b.periodIndex
          )
        })
      }
    } catch (err) {
      console.warn('Polling submission status failed:', err)
    } finally {
      setIsPollingStatus(false)
    }
  }, [formId])

  // Auto-poll while any period requires polling. Doc recommends ~30s cadence.
  useEffect(() => {
    if (!formId) return
    if (currentStep !== 7) return
    const anyRequiresPolling =
      periodSubmissions.length > 0
        ? periodSubmissions.some(p => p.requiresPolling === true)
        : formStatus === 'submitted' || formStatus === 'processing'
    if (!anyRequiresPolling) return

    // Kick off an immediate poll if we just landed here, then settle into 30s cadence.
    let cancelled = false
    const tick = async () => {
      if (cancelled) return
      await pollSubmissionStatus()
    }
    void tick()
    const id = setInterval(() => {
      void tick()
    }, 30000)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [formId, currentStep, formStatus, periodSubmissions, pollSubmissionStatus])

  // ========================================================
  // Submit to HMRC (Step 7)
  // ========================================================
  const startAmendment = () => {
    setIsAmending(true)
    setSubmissionResult(null)
    // The amended return needs a fresh declaration.
    setSubmission(prev => ({
      ...prev,
      amendedReturn: true,
      declarationAccepted: false,
    }))
    handleStepClick(1)
  }

  const cancelAmendment = () => {
    setIsAmending(false)
    setSubmission(prev => ({ ...prev, amendedReturn: false }))
  }

  const handleSubmit = async () => {
    if (!formId) return

    setIsSubmitting(true)
    try {
      // The platform fee is a one-time payment per company + accounting period.
      // Coverage clears the user even if the earlier payment was made under a
      // different filing scope (e.g. CT-only paid, now filing accounts-only for
      // the same period). Fall back to the per-form check if coverage is unknown.
      const coverage = await refreshCoverage()
      const alreadyPaid = coverage?.paid || (await checkPayment(formId))

      if (!alreadyPaid) {
        // A prior payment exists but has lapsed — tell the user when it expired
        // before sending them to the pricing modal to pay again.
        if (coverage?.paidVia) {
          const expiredOn = coverage.paidVia.expiresAt
          toast.error(
            expiredOn
              ? `Your previous payment for this company expired on ${new Date(
                  expiredOn
                ).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}. Please choose a plan to continue.`
              : 'Your previous payment has expired. Please choose a plan to continue.'
          )
          setShowPaymentModal(true)
          return
        }

        // First-time payment — plan already selected? go straight to Stripe.
        const planSlug = selectedPlanSlug || readStoredPlanSlug(formId)
        if (planSlug) {
          try {
            const successUrl = `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}&plan=${planSlug}&formId=${formId}&source=ct600`
            const cancelUrl = `${window.location.origin}/history`

            const checkoutData = await paymentService.createCheckout(planSlug, {
              successUrl,
              cancelUrl,
              customerEmail: user?.email || '',
              formId,
              // Pin to company so the coverage check finds the payment when
              // the user comes back to edit the same company within 12 months.
              ...(setup.companyId ? { companyId: setup.companyId } : {}),
            })

            if (checkoutData.url) {
              window.location.href = checkoutData.url
              return
            }
          } catch (err) {
            console.error('Direct checkout failed, showing plan modal:', err)
          }
        }
        // Fallback: no plan slug or checkout failed — show plan modal
        setShowPaymentModal(true)
        return
      }

      // Complete form first. An amendment skips this: the form keeps its
      // accepted status, and submit validates the return itself.
      if (!isAmending) {
        const completePayload: { companyId?: string } = {}
        if (setup.companyId) completePayload.companyId = setup.companyId
        await ct600Service.completeForm(formId, completePayload)
      }

      // Submit
      const submitPayload: Record<string, unknown> = {
        submittedBy: submission.submittedBy,
        submitterRole: submission.submitterRole,
        declarationAccepted: submission.declarationAccepted,
        governmentGatewayUserId: submission.governmentGatewayUserId,
        governmentGatewayPassword: submission.governmentGatewayPassword,
        amendedReturn: isAmending || (submission.amendedReturn ?? false),
      }
      if (setup.companyId) submitPayload.companyId = setup.companyId
      if (submission.submitterRole === 'agent' && submission.agentReference) {
        submitPayload.agentReference = submission.agentReference
      }

      const response = await ct600Service.submitForm(
        formId,
        submitPayload as any
      )
      const outcome = parseSubmitOutcome(response.response)
      const nextSubmissionResult: {
        status: string
        correlationId?: string
        message?: string
      } = {
        status: outcome.status,
      }
      if (outcome.correlationId)
        nextSubmissionResult.correlationId = outcome.correlationId
      if (outcome.message) nextSubmissionResult.message = outcome.message

      setSubmissionResult(nextSubmissionResult)
      setFormStatus(outcome.status)
      if (outcome.isLpoa !== undefined || outcome.periods) {
        setAggregateStatus(outcome.status)
      }

      // Hydrate per-period state from the submit response so the UI can show
      // per-period status pills immediately, before the first poll.
      if (Array.isArray(outcome.periods) && outcome.periods.length > 0) {
        setPeriodSubmissions(prev => {
          // Prefer freshly-returned data; fall back to existing fields like
          // periodLabel/periodStart/periodEnd from prior state.
          const byIndex = new Map<number, Ct600PeriodSubmission>()
          for (const p of prev) byIndex.set(p.periodIndex, p)
          for (const p of outcome.periods!) {
            const existing = byIndex.get(p.periodIndex) ?? {
              periodIndex: p.periodIndex,
            }
            byIndex.set(p.periodIndex, {
              ...existing,
              ...p,
            } as Ct600PeriodSubmission)
          }
          return Array.from(byIndex.values()).sort(
            (a, b) => a.periodIndex - b.periodIndex
          )
        })
      }

      const submitData = (
        response.response as { data?: { amendmentRejected?: boolean } }
      ).data
      if (submitData?.amendmentRejected) {
        // Stay in amend mode so the user can fix the return and resubmit.
        const reason = outcome.periods
          ?.flatMap(p => p.errors ?? [])
          .find(e => e?.message)?.message
        toast.error(
          `HMRC rejected the amended return${reason ? `: ${reason}` : ''}. Your original accepted return still stands.`
        )
        return
      }
      setIsAmending(false)

      if (outcome.status === 'rejected') {
        toast.error(outcome.message || 'CT600 submission was rejected by HMRC.')
        return
      }

      if (outcome.status === 'accepted') {
        toast.success('CT600 return was accepted by HMRC.')
        setShowReviewPrompt(true)
        return
      }

      toast.success(
        outcome.isLpoa
          ? 'CT600 LPOA submitted — both periods sent to HMRC.'
          : 'CT600 return submitted to HMRC successfully.'
      )
      // Reached HMRC without a rejection — a good moment to ask for a review.
      setShowReviewPrompt(true)
    } catch (err) {
      console.error('Submission failed:', err)
      // HTTP 422 carries useful per-period state in the body — both for
      // partial failures (data.periods[]) and for full-process errors
      // (partialPeriods[]). Treat it as a structured outcome so the UI shows
      // pills + a Retry button rather than discarding the periods.
      const apiErr = err instanceof ApiError ? err : null
      const errBody = apiErr?.response
      if (errBody && typeof errBody === 'object') {
        try {
          const outcome = parseSubmitOutcome(errBody)
          if (outcome.periods && outcome.periods.length > 0) {
            setPeriodSubmissions(prev => {
              const byIndex = new Map<number, Ct600PeriodSubmission>()
              for (const p of prev) byIndex.set(p.periodIndex, p)
              for (const p of outcome.periods!) {
                const existing = byIndex.get(p.periodIndex) ?? {
                  periodIndex: p.periodIndex,
                }
                byIndex.set(p.periodIndex, {
                  ...existing,
                  ...p,
                } as Ct600PeriodSubmission)
              }
              return Array.from(byIndex.values()).sort(
                (a, b) => a.periodIndex - b.periodIndex
              )
            })
          }
          if (outcome.isLpoa !== undefined || outcome.periods) {
            setAggregateStatus(outcome.status)
          }
          // Mark the form as rejected so the Retry path renders. The user
          // can click Retry — backend skips already-accepted periods.
          setSubmissionResult({
            status: outcome.status,
            ...(outcome.correlationId
              ? { correlationId: outcome.correlationId }
              : {}),
            ...(outcome.message ? { message: outcome.message } : {}),
          })
          if (outcome.status === 'rejected') setFormStatus('rejected')
        } catch {
          /* fall through to plain toast */
        }
      }
      toast.error(extractApiError(err, 'Submission failed.'))
    } finally {
      setIsSubmitting(false)
    }
  }

  // ========================================================
  // Load draft
  // ========================================================
  const handleLoadDraft = (draft: CT600Draft) => {
    setSetup(draft.setup)
    // A saved draft already carries a chosen period — treat as confirmed.
    if (draft.setup.accountingPeriodStart && draft.setup.accountingPeriodEnd) {
      setPeriodConfirmed(true)
    }
    setFirstReturnAnswered(true)
    setCompanyInfo(draft.companyInfo)
    setFinancialData(draft.financialData)
    setBalanceSheet(draft.balanceSheet)
    if (draft.previousYear) setPreviousYear(draft.previousYear)
    setTaxComp(draft.taxComputation)
    setSupplementary(draft.supplementary)
    setSubmission(draft.submission)
    if (draft.formId) setFormId(draft.formId)
    if (draft.planSlug) setSelectedPlanSlug(draft.planSlug)
    if (draft.currentStep > 1) {
      setCurrentStep(draft.currentStep)
      const completed = new Set<CT600WizardStepId>()
      for (let i = 1; i < draft.currentStep; i++)
        completed.add(i as CT600WizardStepId)
      setCompletedSteps(completed)
    }
    draftLoadedRef.current = true
    if (draft.formId) {
      navigate(`/ct600-filing/${draft.formId}`, { replace: true })
    }
    toast.success('Draft restored.')
  }

  // ========================================================
  // Save & close
  // ========================================================
  const handleSaveAndClose = async () => {
    // Write to the backend, not just the local draft — reopening the form
    // loads from the API, so a local-only save reads back as empty fields.
    setIsLoading(true)
    try {
      await persistStep(currentStep, { silent: true })
      saveDraft()
      toast.success('Draft saved.')
      navigate('/ct600-filing')
    } catch (err) {
      console.error('Save & close failed:', err)
      saveDraft()
      toast.error(
        extractApiError(err, 'Could not save to the server. Please try again.')
      )
    } finally {
      setIsLoading(false)
    }
  }

  // ========================================================
  // Render step content
  // ========================================================
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <CT600StepSetup
            companies={companies}
            setup={setup}
            onSetupChange={update => {
              setSetup(prev => ({ ...prev, ...update }))
              // Editing the period dates invalidates a prior confirmation.
              if (
                'accountingPeriodStart' in update ||
                'accountingPeriodEnd' in update
              ) {
                setPeriodConfirmed(false)
              }
            }}
            onCompanySelect={handleCompanySelect}
            onLoadDraft={handleLoadDraft}
            isLoadingCompanies={isLoadingCompanies}
            validationErrors={validationErrors}
            selectedPlanSlug={selectedPlanSlug}
            onOpenPlanModal={() => {
              // One-off payment per company is valid for 12 months and covers
              // every subsequent edit/filing on the same company. If the
              // backend says we're still covered, don't open the pricing
              // modal — the user already paid for this company.
              if (isCovered) {
                toast.success(
                  'This company is already paid for — no payment needed.'
                )
                return
              }
              setShowPlanModal(true)
            }}
            companyFlags={companyInfo.flags}
            onCompanyFlagsChange={flags => {
              const wasDormant = companyInfo.flags.isDormant
              setCompanyInfo(prev => ({ ...prev, flags }))
              // If the user just turned Dormant on, the previously selected
              // (non-dormant) plan no longer applies — clear it and open the
              // modal so they can pick a dormant plan. Skipped when the
              // company is already covered: a paid-up company stays covered
              // regardless of which plan slug was picked originally.
              if (!wasDormant && flags.isDormant && !isCovered) {
                setSelectedPlanSlug(null)
                setSetup(prev => ({ ...prev, planSlug: '' }))
                setShowPlanModal(true)
              }
              // If the user turned Dormant off and a dormant plan was held,
              // clear it so they pick a regular plan next.
              if (
                wasDormant &&
                !flags.isDormant &&
                selectedPlanSlug &&
                selectedPlanSlug.toLowerCase().includes('dormant')
              ) {
                setSelectedPlanSlug(null)
                setSetup(prev => ({ ...prev, planSlug: '' }))
              }
            }}
            periodSplit={periodSplit}
            isLoadingPeriodSplit={isLoadingPeriodSplit}
            periodSplitError={periodSplitError}
            isPeriodConfirmed={periodConfirmed}
            isFirstReturn={companyInfo.details.firstReturn}
            isFirstReturnAnswered={firstReturnAnswered}
            onFirstReturnChange={value => {
              setFirstReturnAnswered(true)
              setCompanyInfo(prev => ({
                ...prev,
                details: { ...prev.details, firstReturn: value },
              }))
            }}
            onRefreshPeriodSplit={() => {
              if (!setup.companyNumber) return
              const opts: {
                start?: string
                end?: string
                forceRefresh?: boolean
              } = { forceRefresh: true }
              if (setup.accountingPeriodStart)
                opts.start = setup.accountingPeriodStart
              if (setup.accountingPeriodEnd)
                opts.end = setup.accountingPeriodEnd
              void fetchPeriodSplit(setup.companyNumber, opts)
              // Clicking "Confirm accounting period" unlocks the Next button.
              setPeriodConfirmed(true)
            }}
            onCalculateManualSplit={async () => {
              await handleCalculateManualSplit()
              // Clicking "Confirm accounting period" unlocks the Next button.
              setPeriodConfirmed(true)
            }}
          />
        )
      case 2:
        return (
          <CT600StepCompanyInfo
            companyInfo={companyInfo}
            onCompanyInfoChange={setCompanyInfo}
            validationErrors={validationErrors}
          />
        )
      case 3:
        return (
          <CT600StepFinancials
            financialData={financialData}
            balanceSheet={balanceSheet}
            previousYear={previousYear}
            onFinancialDataChange={setFinancialData}
            onBalanceSheetChange={setBalanceSheet}
            onPreviousYearChange={setPreviousYear}
            accountingPeriodEnd={setup.accountingPeriodEnd}
          />
        )
      case 4:
        return (
          <CT600StepTaxComp
            taxComp={taxComp}
            profitBeforeTax={profitBeforeTax}
            onTaxCompChange={setTaxComp}
          />
        )
      case 5:
        return (
          <CT600StepSupplementary
            supplementary={supplementary}
            onSupplementaryChange={setSupplementary}
            isCheckingRequirements={isCheckingRequirements}
          />
        )
      case 6:
        return (
          <CT600StepReview
            companyInfo={companyInfo}
            financialData={financialData}
            balanceSheet={balanceSheet}
            taxComp={taxComp}
            supplementary={supplementary}
            calculationResult={calculationResult}
            calculationBreakdown={calculationBreakdown}
            ct600Boxes={ct600Boxes}
            formStatus={formStatus}
            isCalculating={isCalculating}
            onCalculate={handleCalculate}
          />
        )
      case 7:
        return (
          <CT600StepSubmit
            submission={submission}
            onSubmissionChange={setSubmission}
            isSubmitting={isSubmitting}
            submissionResult={submissionResult}
            onSubmit={handleSubmit}
            hasCalculated={calculationResult !== null}
            isPaid={isPaid || isCovered}
            selectedPlan={selectedPlanSlug}
            formStatus={formStatus}
            formId={formId}
            readyForSubmission={formReadiness.readyForSubmission}
            validationErrors={formReadiness.validationErrors}
            onGoToStep={handleStepClick}
            directors={periodSplit?.directors ?? []}
            periodSubmissions={periodSubmissions}
            aggregateStatus={aggregateStatus}
            isLpoa={periodSplit?.isLpoa ?? periodSubmissions.length > 1}
            isPollingStatus={isPollingStatus}
            onPollStatus={pollSubmissionStatus}
            companyName={setup.companyName}
            periodStart={setup.accountingPeriodStart}
            periodEnd={setup.accountingPeriodEnd}
            amendMode={isAmending}
            onStartAmendment={startAmendment}
            onCancelAmendment={cancelAmendment}
          />
        )
      default:
        return null
    }
  }

  // ========================================================
  // JSX
  // ========================================================

  if (isInitializing) {
    return (
      <AuthenticatedLayout>
        <div className='flex min-h-screen items-center justify-center'>
          <Loader2 className='h-8 w-8 animate-spin text-gray-400' />
        </div>
      </AuthenticatedLayout>
    )
  }

  return (
    <AuthenticatedLayout>
      <div className='min-h-screen bg-gradient-to-b from-gray-50 to-white'>
        {/* Header */}
        <div className='border-b border-gray-200 bg-white sticky top-0 z-40'>
          <div className='mx-auto px-4 sm:px-6 py-4 flex items-center gap-3'>
            <button
              onClick={() => navigate('/history')}
              className='rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors shrink-0'
            >
              <ArrowLeft className='h-5 w-5' />
            </button>
            <div className='min-w-0 flex-1'>
              <h1 className='text-base sm:text-lg font-bold text-gray-900 truncate'>
                CT600 Corporation Tax Return
              </h1>
              <p className='text-xs text-gray-500 truncate'>
                {setup.companyName || 'Select a company'}{' '}
                {setup.accountingPeriodStart && setup.accountingPeriodEnd
                  ? `— ${new Date(setup.accountingPeriodStart).toLocaleDateString('en-GB')} to ${new Date(setup.accountingPeriodEnd).toLocaleDateString('en-GB')}`
                  : ''}
              </p>
            </div>
            <button
              type='button'
              onClick={() => setDetailsOpen(true)}
              className='inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-50 transition'
            >
              <Building2 className='h-4 w-4' />
              <span className='hidden sm:inline'>Details</span>
            </button>
          </div>
        </div>

        {/* Main content */}
        <div className='mx-auto max-w-[1600px] px-4 sm:px-6 py-6 sm:py-8'>
          {/* Steps as a full-width tab header */}
          <div className='mb-6'>
            <CT600StepNav
              steps={ct600Steps}
              currentStep={currentStep}
              completedSteps={completedSteps}
              onStepClick={handleStepClick}
              variant='tabs'
            />
          </div>

          {/* Full-width step content */}
          <div className='min-w-0'>
              <div className='rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 xl:p-8 shadow-sm'>
                {renderStepContent()}

                {/* Navigation buttons */}
                {!(
                  !isAmending &&
                  currentStep === 7 &&
                  (formStatus === 'accepted' ||
                    formStatus === 'submitted' ||
                    formStatus === 'processing' ||
                    formStatus === 'completed' ||
                    submissionResult?.status === 'accepted')
                ) && (
                  <div className='mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-6'>
                    <button
                      type='button'
                      onClick={handlePrevious}
                      disabled={currentStep === 1 || isLoading}
                      className='flex items-center gap-2 rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                    >
                      <ArrowLeft className='h-4 w-4' />
                      Previous
                    </button>

                    <button
                      type='button'
                      onClick={handleSaveAndClose}
                      className='flex items-center gap-2 rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors'
                    >
                      <Save className='h-4 w-4' />
                      Save & Close
                    </button>

                    {currentStep < 7 ? (
                      (() => {
                        const blockedOnCalculation =
                          currentStep === 6 && !hasValidCalculation
                        const blockedOnPeriodConfirm =
                          currentStep === 1 &&
                          setup.returnType === 'tax-only' &&
                          !periodConfirmed
                        return (
                          <button
                            type='button'
                            onClick={handleNext}
                            disabled={
                              isLoading ||
                              blockedOnCalculation ||
                              blockedOnPeriodConfirm
                            }
                            title={
                              blockedOnPeriodConfirm
                                ? 'Please click Confirm accounting period before proceeding.'
                                : blockedOnCalculation
                                  ? 'Please click Calculate Tax above before proceeding.'
                                  : undefined
                            }
                            className='flex items-center gap-2 rounded-lg bg-gray-900 px-6 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                          >
                            {isLoading ? (
                              <Loader2 className='h-4 w-4 animate-spin' />
                            ) : (
                              <>
                                Next
                                <ArrowRight className='h-4 w-4' />
                              </>
                            )}
                          </button>
                        )
                      })()
                    ) : (
                      <div /> // Placeholder - submit button is inside step 7
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

      <WizardDetailsModal open={detailsOpen} onClose={() => setDetailsOpen(false)} title='Company Details'>
        <CT600WizardSidebar company={selectedCompany} setup={setup} formStatus={formStatus} width='w-full' />
      </WizardDetailsModal>

      {/* Modals */}
      <CT600PlanSelectionModal
        isOpen={showPlanModal || showPaymentModal}
        onClose={() => {
          setShowPlanModal(false)
          setShowPaymentModal(false)
        }}
        formId={formId || currentFormId || ''}
        companyId={setup.companyId || ''}
        onPlanSelected={slug => {
          setSelectedPlanSlug(slug)
          setSetup(prev => ({ ...prev, planSlug: slug }))
          setValidationErrors(prev => {
            const next = { ...prev }
            delete next.planSlug
            return next
          })
          setShowPlanModal(false)
          if (formId) persistPlanSlug(formId, slug)
        }}
        selectOnly={!formId}
        dormantOnly={companyInfo.flags.isDormant}
      />
      <TrustpilotReviewModal
        open={showReviewPrompt}
        onClose={() => setShowReviewPrompt(false)}
        filingLabel='CT600 return'
      />
    </AuthenticatedLayout>
  )
}
